import React, { useState } from 'react';
import { FileDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { isPro as checkIsPro } from '../../utils/planPermissions';
import ProFeatureButton from '../ProFeatureButton';
import analyticsApi from '../../api/analyticsApi';

interface ExportExcelButtonProps {
  onExport: () => Promise<void> | void;
  disabled?: boolean;
  noDataMessage?: string;
  filename?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Reusable Excel export button that handles PRO/Premium permissions
 * Shows either export button or PRO upgrade button based on user subscription
 */
const ExportExcelButton: React.FC<ExportExcelButtonProps> = ({
  onExport,
  disabled = false,
  noDataMessage = 'No hay datos para exportar',
  filename = 'datos_exportados.xlsx',
  className = '',
  style = {}
}) => {
  const { currentUser } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  
  // Check if user has PRO or Premium subscription
  const hasProAccess = checkIsPro(currentUser?.subscription);

  // Exportar la base de clientes = SOLO el Dueño (decisión Carlos 4-jul-2026): un gerente
  // o empleado no debe poder llevarse el Excel. baseRole ausente (contexto de plataforma o
  // respuesta vieja) no oculta — fail-open, consistente con el resto de gates por rol.
  if (currentUser?.baseRole && currentUser.baseRole !== 'owner') {
    return null;
  }

  const handleExport = async () => {
    if (disabled || isExporting) return;
    
    try {
      setIsExporting(true);
      await onExport();
      // Deja constancia de que ESTE negocio exportó hoy (una fila al día en audit_log). No
      // bloquea ni se espera: va después de que la descarga ya se generó.
      analyticsApi.trackExport(filename);
    } finally {
      setIsExporting(false);
    }
  };

  // Show ProFeatureButton if user doesn't have PRO/Premium access
  if (!hasProAccess) {
    return (
      <div className={className} style={style}>
        <ProFeatureButton>
          Exportar Excel
        </ProFeatureButton>
      </div>
    );
  }

  return (
    <button 
      onClick={handleExport} 
      disabled={isExporting || disabled}
      className={`inline-flex items-center px-3 py-2 border border-line shadow-sm text-sm leading-4 font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      style={style}
      title={disabled ? noDataMessage : filename}
    >
      {isExporting ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-content-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Exportando...
        </>
      ) : (
        <>
          <FileDown className="h-4 w-4 mr-2" />
          Exportar Excel
        </>
      )}
    </button>
  );
};

export default ExportExcelButton;